/**
 * Sentry SDK Configuration
 * Optimized for free tier (5,000 events/month)
 */

import * as Sentry from '@sentry/react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router'
import { useEffect } from 'react'

/**
 * Initialize Sentry with optimized filters for free tier
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    // eslint-disable-next-line no-console -- Startup warning for observability config
    console.warn('Sentry DSN not configured. Error tracking disabled.')
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // Enable performance monitoring (optional, uses quota)
    integrations: [
      // React Router integration for better error context
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Replay integration for session recording (uses additional quota)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring sample rate (0.0 to 1.0)
    // Lower rate = fewer events = more quota for errors
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay sample rate
    // Only replay sessions with errors to save quota
    replaysSessionSampleRate: 0.0, // Don't replay normal sessions
    replaysOnErrorSampleRate: 0.3, // Replay 30% of sessions with errors (cost optimization)

    // Filter events before sending to Sentry
    beforeSend(event, hint) {
      // Don't send events in development (optional)
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- Dev-only debugging for Sentry events
        console.log('Sentry event (not sent in dev):', event)
        return null
      }

      // Filter out non-critical errors
      const error = hint.originalException

      if (error instanceof Error) {
        // Ignore network errors (often user connectivity issues)
        if (
          error.message.includes('NetworkError') ||
          error.message.includes('Failed to fetch') ||
          error.message.includes('Network request failed')
        ) {
          return null
        }

        // Ignore AbortController errors (user cancelled requests)
        if (error.name === 'AbortError') {
          return null
        }

        // Ignore ResizeObserver errors (browser quirk, not actionable)
        if (error.message.includes('ResizeObserver')) {
          return null
        }
      }

      // Sanitize sensitive data
      if (event.request) {
        // Remove authorization headers
        if (event.request.headers) {
          delete event.request.headers.Authorization
          delete event.request.headers.Cookie
        }

        // Remove query params that might contain tokens
        if (event.request.url) {
          const url = new URL(event.request.url)
          if (url.searchParams.has('token')) {
            url.searchParams.set('token', '[Filtered]')
            event.request.url = url.toString()
          }
        }
      }

      // Sanitize user data (PII compliance)
      if (event.user) {
        if (event.user.email) {
          event.user.email = '[Filtered]'
        }
        if (event.user.ip_address) {
          event.user.ip_address = '[Filtered]'
        }
      }

      return event
    },

    // Filter breadcrumbs to reduce noise
    beforeBreadcrumb(breadcrumb) {
      // Don't log console breadcrumbs in production
      if (breadcrumb.category === 'console' && import.meta.env.PROD) {
        return null
      }

      // Don't log debug-level HTTP requests
      if (
        breadcrumb.category === 'fetch' &&
        breadcrumb.data?.url?.includes('/health')
      ) {
        return null
      }

      return breadcrumb
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',

      // Random plugins/extensions
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',

      // Facebook related errors
      'fb_xd_fragment',

      // ISP/Proxy injected errors
      '__gCrWeb',
      '__gCrWeb.autofill',

      // Webkit/Safari errors
      'Non-Error promise rejection captured',
    ],

    // Don't capture errors from these URLs
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  })
}

/**
 * Set user context for error tracking
 * Note: Email is not sent to preserve privacy (LGPD compliance)
 */
export function setUserContext(user: {
  id: string
  email?: string
  username?: string
}) {
  Sentry.setUser({
    id: user.id,
    // email removed for PII compliance
    username: user.username,
  })
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext() {
  Sentry.setUser(null)
}

/**
 * Add custom context to errors
 */
export function addBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level?: Sentry.SeverityLevel
) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: level || 'info',
  })
}

/**
 * Manually capture an exception
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
) {
  Sentry.captureException(error, {
    contexts: {
      custom: context,
    },
  })
}

/**
 * Manually capture a message
 */
export function captureMessage(message: string, level?: Sentry.SeverityLevel) {
  Sentry.captureMessage(message, level || 'info')
}
